<template>
  <model-form
    ref="modelForm"
    :model="ticketType"
    modal-type="modal"
    width="550px"
  >
    <q-tabs
      v-model="tab"
      align="justify"
      dense
    >
      <q-tab
        name="details"
        label="Details"
      />
      <q-tab
        v-if="$isPermitted($permissions.StudioReporting.View)"
        name="reporting"
        label="Reporting"
      />
    </q-tabs>
    <q-tab-panels v-model="tab">
      <q-tab-panel name="details">
        <div class="column">
          <div class="row q-col-gutter-sm items-start">
            <model-form-input
              field="name"
              class="col-6"
              label="Name"
              :rules="[val => !!val || 'Field is required']"
              autofocus
            />
            <model-form-input
              field="price"
              class="col"
              :label="currentSite.ticketPriceLabel"
              :prefix="currentCircuit.localeCurrencySymbol"
              type="number"
              step="0.25"
              min="0"
              :rules="[val => val !== null || 'Field is required']"
            />
          </div>
          <div class="row q-col-gutter-sm items-start">
            <model-form-select
              field="excludeRatings"
              value-type="[String]"
              class="col-12"
              label="Exclude Ratings"
              new-value-mode="add-unique"
              multiple
              use-input
              use-chips
              @keyup.enter.stop
            >
              <q-tooltip>
                Exclude this ticket type from being sold for movies with the given ratings.
              </q-tooltip>
            </model-form-select>
          </div>
          <div class="row q-col-gutter-sm items-start">
            <q-toggle
              :model-value="ticketType.userClass !== null"
              class="col-6"
              label="Limit to User Class"
              bottom-slots
              :disable="ticketType.isTranslation"
              @update:model-value="(value) => value ? (ticketType.userClass = userClasses.data[0]) : (ticketType.userClass = null) "
            />
            <model-form-select
              field="userClass"
              class="col"
              :label="$t('user.userClass')"
              :options="userClasses.data"
              option-label="name"
              :display-value="ticketType.userClass && ticketType.userClass.name || null"
            />
          </div>
          <div class="row q-col-gutter-sm items-start">
            <q-toggle
              :model-value="ticketType.membershipType !== null"
              class="col-6"
              label="Limit to Membership Type"
              bottom-slots
              :disable="ticketType.isTranslation"
              @update:model-value="(value) => value ? (ticketType.membershipType = membershipTypes.data[0]) : (ticketType.membershipType = null) "
            />
            <model-form-select
              field="membershipType"
              class="col"
              label="Membership Type"
              :options="membershipTypes.data"
              option-label="name"
              :disable="ticketType.isTranslation"
              :display-value="ticketType.membershipType && ticketType.membershipType.name || null"
            />
          </div>
          <div class="row q-col-gutter-sm items-start">
            <model-form-toggle
              field="ticketTypeOptions.has_custom_display_name"
              value-type="Boolean"
              class="col-6"
              label="Has Custom Display Name"
            >
              <q-tooltip>Display name is what is shown to the user during checkout flow and on receipts</q-tooltip>
            </model-form-toggle>
            <model-form-input
              v-if="ticketType.ticketTypeOptions.has_custom_display_name"
              field="ticketTypeOptions.custom_display_name"
              value-type="String"
              class="col-6"
              label="Custom Display Name"
              :rules="[val => !!val || 'Field is required']"
            />
            <model-form-input
              v-else
              field="displayName"
              readonly
              class="col-6"
              label="Display Name"
            />
          </div>
          <model-form-checkbox
            v-if="!ticketType.membershipType"
            field="ticketTypeOptions.requires_authorization"
            value-type="Boolean"
            class="col-6"
            label="Requires Authorization At Register"
          >
            <q-tooltip>With this setting enabled, employees require authorization from an Admin user to allow selling this ticket type.</q-tooltip>
          </model-form-checkbox>

          <model-form-checkbox
            v-if="currentSite.dboxEnabledValue"
            field="isDboxTicketTypeValue"
            value-type="Boolean"
            class="col-6"
            label="Is DBox Ticket Type"
          >
            <q-tooltip>
              Purchasing a ticket of this ticket type will enable DBox for the seat selected.
            </q-tooltip>
          </model-form-checkbox>
          <div
            class="row q-col-gutter-sm items-center"
          >
            <model-form-checkbox
              field="ticketTypeOptions.applies_to_specific_seats"
              value-type="Boolean"
              class="col-6"
              label="Applies To Seat Chart Areas"
            />
            <div class="col-6 row q-gutter-sm" />
            <q-tooltip>
              In general, any ticket type can be used at any seat.
              <br><br>
              If there are special seats with different prices, they need their own ticket type.
              <br><br>
              By checking "Applies To Specific Seats" this ticket type will not be able to be used,
              <br>
              except for seats that have been a matching area category that explicitly allows this ticket type.
              <br><br>
              Once checked, this ticket type will be able to be applied to specific seats that with a matching area category.
            </q-tooltip>
          </div>
          <div class="row q-col-gutter-sm items-start">
            <model-form-select
              v-if="ticketType.ticketTypeOptions.applies_to_specific_seats"
              class="col-12"
              field="seatChartAreaCategoryIds"
              label="Seat Chart Areas"
              :options="seatChartAreaCategories.data"
              :option-label="(category) => `${category.excludeTicketTypes ? 'Exclude from' : 'Allow in'}: ${category.name}`"
              option-value="id"
              emit-value
              map-options
              multiple
              use-chips
            />
          </div>
          <div class="row q-col-gutter-sm items-start">
            <model-form-select
              field="channels"
              class="col-12"
              label="Channels"
              :options="$mh.asOptions(CHANNELS.ALL)"
              map-options
              emit-value
              multiple
              use-chips
              clearable
              :decode="channels => $mh.objectToArray(channels)"
              :encode="enabledChannels => $mh.arrayToObject(enabledChannels || [], { allKeys: CHANNELS.ALL })"
            >
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <div class="column">
                      <span
                        class="text-body-em"
                        style="font-size: 0.9rem;"
                      >{{ scope.opt.label }}</span>
                      <span class="text-caption">{{ CHANNELS_TOOLTIPS[scope.opt.value] }}</span>
                    </div>
                  </q-item-section>
                </q-item>
              </template>
              <q-tooltip
                anchor="top middle"
                self="bottom middle"
              >
                This Ticket Type will be available for purchase through the selected channels.
              </q-tooltip>
            </model-form-select>
            <model-form-checkbox
              v-if="ticketType.channels.website && currentSite.ceaCardEnabled"
              field="isCeaCard"
              class="col-6"
              label="CEA Card Ticket"
            >
              <q-tooltip>
                This ticket can be redeemed with a CEA card.
              </q-tooltip>
            </model-form-checkbox>
          </div>
        </div>
      </q-tab-panel>
      <q-tab-panel
        v-if="$isPermitted($permissions.StudioReporting.View)"
        name="reporting"
      >
        <div class="column">
          <div class="row q-col-gutter-sm items-start">
            <model-form-select
              field="ticketTypeOptions.reporting_priority"
              value-type="[String]"
              map-options
              emit-value
              multiple
              use-chips
              class="col-12"
              label="Reporting Priority"
              :options="$mh.asOptions(TICKET_TYPE_REPORTING_OPTIONS.ALL)"
            >
              <q-tooltip>
                Prioritise sources used to determine the reporting name of the ticket type.
                If the source does not apply or have a reporting name set, the next option will be used instead.
              </q-tooltip>
            </model-form-select>
          </div>
          <div class="row q-col-gutter-sm items-start">
            <model-form-select
              field="ticketTypeOptions.box_office_reporting_priority"
              value-type="[String]"
              map-options
              emit-value
              multiple
              use-chips
              class="col-12"
              label="Box Office Reporting Priority"
              :options="$mh.asOptions(TICKET_TYPE_REPORTING_OPTIONS.ALL)"
            >
              <q-tooltip>
                Prioritise sources used to determine the box office reporting name of the ticket type.
                If the source does not apply or have a reporting name set, the next option will be used instead.
              </q-tooltip>
            </model-form-select>
          </div>
          <div class="row q-col-gutter-sm items-start">
            <model-form-toggle
              field="ticketTypeOptions.has_custom_reporting_name"
              value-type="Boolean"
              class="col-6"
              label="Has Reporting Name"
            >
              <q-tooltip>
                The reporting name used to group ticket types for internal reporting
              </q-tooltip>
            </model-form-toggle>
            <model-form-input
              field="ticketTypeOptions.custom_reporting_name"
              value-type="String"
              class="col-6"
              label="Reporting Name"
              :disable="!ticketType.ticketTypeOptions.has_custom_reporting_name"
            />
          </div>
          <div class="row q-col-gutter-sm items-start">
            <model-form-toggle
              field="ticketTypeOptions.has_custom_box_office_reporting_name"
              value-type="Boolean"
              class="col-6"
              label="Has Box Office Reporting Name"
            >
              <q-tooltip>
                The reporting name used to group ticket types for box office reporting
              </q-tooltip>
            </model-form-toggle>
            <model-form-input
              field="ticketTypeOptions.custom_box_office_reporting_name"
              value-type="String"
              class="col-6"
              label="Box Office Reporting Name"
              :disable="!ticketType.ticketTypeOptions.has_custom_box_office_reporting_name"
            />
          </div>
          <div class="row q-col-gutter-sm items-start">
            <model-form-toggle
              field="ticketTypeOptions.has_min_reported_price"
              value-type="Boolean"
              class="col-6"
              label="Has Minimum Reporting Value"
            />
            <model-form-input
              field="ticketTypeOptions.min_reported_price"
              value-type="Number"
              :disable="!ticketType.ticketTypeOptions.has_min_reported_price"
              class="col"
              :label="'Minimum Reporting Value (' + currentSite.ticketTypeReportingSuffix + ')'"
              :prefix="currentCircuit.localeCurrencySymbol"
              type="number"
              min="0"
              step="0.25"
              :rules="[val => val !== null || 'Field is required']"
            />
            <q-tooltip>
              With this enabled, the ticket type price recorded in the BOR reporting will be at least the value specified.
            </q-tooltip>
          </div>
          <div class="row q-col-gutter-sm items-start">
            <model-form-toggle
              field="ticketTypeOptions.has_max_reported_price"
              value-type="Boolean"
              class="col-6"
              label="Has Maximum Reporting Value"
            />
            <model-form-input
              field="ticketTypeOptions.max_reported_price"
              value-type="Number"
              :disable="!ticketType.ticketTypeOptions.has_max_reported_price"
              class="col"
              :label="'Maximum Reporting Value (' + currentSite.ticketTypeReportingSuffix + ')'"
              :prefix="currentCircuit.localeCurrencySymbol"
              type="number"
              min="0"
              step="0.25"
              :rules="[val => val !== null || 'Field is required']"
            />
            <q-tooltip>
              With this enabled, the ticket type price recorded in the BOR reporting will be capped at the value specified.
            </q-tooltip>
          </div>
          <div class="row q-col-gutter-sm items-start">
            <model-form-toggle
              field="excludeFromReporting"
              class="col-12"
              label="Is Excluded From Box Office Reporting"
            />
            <q-tooltip>
              With this enabled, tickets 'sold' under this ticket type will not be reported in BORs such as advanced screenings for staff.
            </q-tooltip>
          </div>
        </div>
        <div class="row q-col-gutter-sm items-start q-mt-lg">
          <model-form-checkbox
            field="ticketTypeOptions.report_price_after_discount"
            value-type="Boolean"
            class="col-6"
            label="Report Price After Discount"
          >
            <q-tooltip>
              When computing box office revenue, use the price after discounts have been applied.
              If this is false, the full ticket price will be used. If it is true, the price reduced
              by the discount amount will be used.<br><br>

              An example is a membership that gets 50% off tickets. If this is a paid membership,
              then the studios will required you to report the full ticket price, since money is
              collected elsewhere for this benefit.<br><br>

              In the case this is a standard ticket, it may be alright to report after a discount is applied.
              For example a discount that is part of a free promotion to see a showing that is expected to
              be lightly attended.<br><br>

              The conservative thing to do, is to leave this false and pay the rental fee on the full ticket
              price.
            </q-tooltip>
          </model-form-checkbox>
          <model-form-checkbox
            field="ticketTypeOptions.report_price_after_voucher"
            value-type="Boolean"
            class="col-6"
            label="Report Price After Voucher"
          >
            <q-tooltip>
              When computing box office revenue, use the price after vouchers have been applied.
              If this is false, the full ticket price will be used. If it is true, the price reduced
              by the applied voucher will be used.<br><br>

              An example is a membership that gets free tickets. If this is a paid membership, then
              the studios will required you to report the full ticket price, since money is collected
              elsewhere for this benefit.<br><br>

              In the case this is a standard ticket, it may be alright to report after a discount is applied.
              For example a discount that is part of a free promotion to see a showing that is expected to
              be lightly attended.<br><br>

              The conservative thing to do, is to leave this false and pay the rental fee on the full ticket
              price.
            </q-tooltip>
          </model-form-checkbox>
        </div>
      </q-tab-panel>
    </q-tab-panels>
  </model-form>
</template>

<script>

import { CHANNELS, TICKET_TYPE_REPORTING_OPTIONS } from 'src/models/showing/TicketType'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'

const CHANNELS_TOOLTIPS = {
  [CHANNELS.KIOSK]: 'Ticket will be available for purchase through the kiosk.',
  [CHANNELS.REGISTER]: 'Ticket will be available for purchase in person.',
  [CHANNELS.WEBSITE]: 'Ticket will be available for purchase through the website and app.'
}

export default {
  name: 'TicketTypeForm',
  components: {
    ModelForm
  },
  data () {
    return {
      tab: 'details'
    }
  },
  configuration: ['currentCircuit', 'currentSite', 'userClasses', 'membershipTypes', 'seatChartAreaCategories'],
  models: {
    ticketType: {
      userClass: true,
      libraryChildSiteIds: true,
      seatChartAreaCategoryIds: true,
      modelAudits: true
    }
  },
  created () {
    _.extend(this, { CHANNELS, CHANNELS_TOOLTIPS, TICKET_TYPE_REPORTING_OPTIONS })
  },
  trackPage: true
}

</script>
